import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ErrorDetailContainer } from "../../../api/config";
import {
    AccountService,
    GatewayNumberDto,
    GatewayNumberStatus,
    ProjectDetailDto,
    ProjectDto,
    SmsCampaignService,
} from "../../../api/psc";
import {
    AppTimePicker,
    CardInformation,
    Spinner,
} from "../../../components/exports";
import SmsCampaignTextArea from "../../smsCampaign/components/SmsCampaignTextArea";

export default function ProjectDetailSms(props: {
    projects: ProjectDto[] | ProjectDetailDto[];
    onSent?: () => void;
}) {
    const [fetchingGatewayNumber, setFetchingGatewayNumber] =
        useState<boolean>(true);
    const [gatewayNumbers, setGatewayNumbers] = useState<GatewayNumberDto[]>(
        []
    );
    const [template, setTemplate] = useState<string>("");
    const [gatewayNumber, setGatewayNumber] = useState<number>(null);
    const [dueDateType, setDueDateType] = useState<number>(1);
    const [dueDate, setDueDate] = useState<string>(null);
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
    const [sent, setSent] = useState<boolean>(false);
    const [error, setError] = useState<string>(null);

    const [requiredPartCount, setRequiredPartCount] = useState<number | null>(
        null
    );
    const [availablePartCount, setAvailablePartCount] = useState<number | null>(
        null
    );

    useEffect(() => {
        SmsCampaignService.listGatewayNumbers("1").then((gatewayNumbers) => {
            const publicGatewayNumbers = gatewayNumbers.filter(
                (gn) => gn.status === GatewayNumberStatus.PUBLIC
            );
            setGatewayNumbers(publicGatewayNumbers);
            setGatewayNumber(publicGatewayNumbers[0].id);
            setFetchingGatewayNumber(false);
        });
    }, []);

    const handleTemplateChange = (value: string) => {
        setTemplate(value);
    };

    const handleGatewayNumberChange = (event: SelectChangeEvent<number>) => {
        setGatewayNumber(event.target.value as number);
    };

    const handleDueDateTypeChange = (event: SelectChangeEvent<number>) => {
        setDueDateType(event.target.value as number);
    };

    const handleDateChange = (dueDate: string) => {
        setDueDate(dueDate);
    };

    const isSmsCampaignValid = template.length > 0 && gatewayNumber !== null;

    const handleSendCampaign = () => {
        setRequestInProgress(true);
        SmsCampaignService.createSmsCampaign("1", {
            definition: {
                gatewayNumberId: gatewayNumber,
                template: template,
                scheduleDate: dueDate,
                dryRun: false,
            },
            projectIdDestinations: props.projects.map((p) => {
                return {
                    projectHashId: p.hashId,
                };
            }),
        })
            .then((r) => {
                setSent(true);
            })
            .catch((e: ErrorDetailContainer) => {
                if (e.hasError("InsufficientSmsPartBalance")) {
                    checkSmsCampaignRequirements();
                }
                setError(e.getFirstError());
            })
            .finally(() => {
                setRequestInProgress(false);
                if (props.onSent) {
                    props.onSent();
                }
            });
    };

    const checkSmsCampaignRequirements = () => {
        Promise.all([
            AccountService.getAccountDetail("1"),
            SmsCampaignService.createSmsCampaign("1", {
                definition: {
                    gatewayNumberId: gatewayNumber,
                    template: template,
                    scheduleDate: dueDate,
                    dryRun: true,
                },
                projectIdDestinations: props.projects.map((p) => {
                    return {
                        projectHashId: p.hashId,
                    };
                }),
            }),
        ]).then((r) => {
            setAvailablePartCount(r[0].partCount);
            setRequiredPartCount(r[1].totalPartCount);
        });
    };

    if (props.projects.some((p) => !p.canVisit)) {
        return (
            <Alert severity="error">
                شما اجازه ارسال به این پروژه را ندارید
            </Alert>
        );
    }

    if (sent) {
        return <Alert severity="success">پیامک در صف ارسال قرار گرفت</Alert>;
    }

    if (error) {
        return (
            <Stack spacing={2}>
                <Alert severity="error">{error}</Alert>

                {requiredPartCount !== null && availablePartCount !== null && (
                    <>
                        <CardInformation
                            title="شارژ مورد نیاز"
                            value={requiredPartCount}
                        />
                        <CardInformation
                            title="شارژ حساب کاربری"
                            value={availablePartCount}
                        />
                    </>
                )}
            </Stack>
        );
    }

    if (fetchingGatewayNumber) {
        return <Spinner />;
    }

    if (props.projects.length <= 1) {
        return <Alert severity="warning">
            این بخش به صورت موقت غیر فعال شده است
        </Alert>;
    }

    return (
        <Box>
            <SmsCampaignTextArea
                template={template}
                onTextChange={handleTemplateChange}
            />

            <FormControl fullWidth sx={{ my: 2 }}>
                <InputLabel>انتخاب سرشماره</InputLabel>
                <Select
                    label="انتخاب سرشماره"
                    value={gatewayNumber}
                    onChange={handleGatewayNumberChange}
                >
                    {gatewayNumbers.map((gn) => (
                        <MenuItem key={gn.id} value={gn.id}>
                            {gn.displayName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>تاریخ ارسال</InputLabel>
                <Select
                    label="تاریخ ارسال"
                    value={dueDateType}
                    onChange={handleDueDateTypeChange}
                >
                    <MenuItem value={1}>ارسال بلافاصله</MenuItem>
                    <MenuItem value={0}>تاریخ انتخابی</MenuItem>
                </Select>
            </FormControl>

            {dueDateType === 0 && (
                <Box sx={{ mb: 2 }}>
                    <AppTimePicker
                        label="تاریخ ارسال"
                        onChange={handleDateChange}
                        initialValue={dueDate}
                    />
                </Box>
            )}

            <LoadingButton
                variant="contained"
                disabled={!isSmsCampaignValid}
                loading={requestInProgress}
                onClick={handleSendCampaign}
            >
                ارسال پیامک
            </LoadingButton>
        </Box>
    );
}
